import {
    ArrowDownTrayIcon,
    EyeIcon,
    PencilSquareIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import React from 'react';
import { FilesTooltip, useFilesConfig } from './config';
import { isMondayStl, isPreviewableAsset } from './fileTypes';
import type { Asset } from './types';
import { formatCreatedDate } from './utils';

interface FileRowProps {
    asset: Asset;
    onPreviewFile: (asset: Asset) => void;
    onDownloadFile: (asset: Asset) => Promise<void>;
    onDeleteFile?: (asset: Asset) => void;
    onRenameFile?: (asset: Asset) => void;
    isSelected?: boolean;
    onToggleSelect?: (assetId: string) => void;
    selectionMode?: boolean;
}

const FileRow: React.FC<FileRowProps> = ({
    asset,
    onPreviewFile,
    onDownloadFile,
    onDeleteFile,
    onRenameFile,
    isSelected,
    onToggleSelect,
    selectionMode
}) => {
    const { showUploadedBy, cbctProcessingEnabled } = useFilesConfig();
    const canPreview = isPreviewableAsset(asset, { cbctProcessing: cbctProcessingEnabled });
    const showDisabledPreview = isMondayStl(asset);
    const assetKey = String(asset.assetId);
    // Rename + delete share the same gate: only GCS-backed files
    const canMutate = asset.source === 'gcs' && Boolean(asset.patientFileId);

    return (
        <div
            className={`flex items-center justify-between rounded px-4 py-3 border transition-colors ${
                isSelected
                    ? 'bg-blue-50 border-blue-300'
                    : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
        >
            {selectionMode && onToggleSelect && (
                <label className="flex items-center mr-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={isSelected ?? false}
                        onChange={() => onToggleSelect(assetKey)}
                        aria-label={`Select ${asset.name}`}
                        className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900 cursor-pointer"
                    />
                </label>
            )}
            <div className="flex-1 min-w-0">
                <FilesTooltip content={asset.name}>
                    <span className="text-sm text-gray-700 truncate block font-medium">
                        {asset.name}
                    </span>
                </FilesTooltip>
                {asset.createdAt && (
                    <span className="text-xs text-gray-500 mt-1 block">
                        {formatCreatedDate(asset.createdAt)}
                        {showUploadedBy && asset.source === 'gcs' && asset.uploadedBy && (
                            <> · By {asset.uploadedBy.name}</>
                        )}
                    </span>
                )}
            </div>
            <div className="flex items-center gap-2 ml-4">
                {canPreview ? (
                    <FilesTooltip content="Preview">
                        <button
                            onClick={() => onPreviewFile(asset)}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                        >
                            <EyeIcon className="w-5 h-5" />
                        </button>
                    </FilesTooltip>
                ) : showDisabledPreview ? (
                    <FilesTooltip content="3D preview is not available for files stored on Monday.com. Download to view.">
                        <span className="text-gray-200 p-1 cursor-not-allowed">
                            <EyeIcon className="w-5 h-5" />
                        </span>
                    </FilesTooltip>
                ) : null}
                <FilesTooltip content="Download">
                    <button
                        onClick={async () => {
                            try {
                                await onDownloadFile(asset);
                            } catch {
                                // download error silently ignored
                            }
                        }}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                    >
                        <ArrowDownTrayIcon className="w-5 h-5" />
                    </button>
                </FilesTooltip>
                {onRenameFile &&
                    (canMutate ? (
                        <FilesTooltip content="Edit">
                            <button
                                onClick={() => onRenameFile(asset)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                                <PencilSquareIcon className="w-5 h-5" />
                            </button>
                        </FilesTooltip>
                    ) : (
                        <FilesTooltip content="This item cannot be renamed">
                            <span className="text-gray-200 p-1 cursor-not-allowed">
                                <PencilSquareIcon className="w-5 h-5" />
                            </span>
                        </FilesTooltip>
                    ))}
                {onDeleteFile &&
                    (canMutate ? (
                        <FilesTooltip content="Delete">
                            <button
                                onClick={() => onDeleteFile(asset)}
                                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                            >
                                <TrashIcon className="w-5 h-5" />
                            </button>
                        </FilesTooltip>
                    ) : (
                        <FilesTooltip content="This item cannot be deleted">
                            <span className="text-gray-200 p-1 cursor-not-allowed">
                                <TrashIcon className="w-5 h-5" />
                            </span>
                        </FilesTooltip>
                    ))}
            </div>
        </div>
    );
};

export { FileRow };
